import Decimal from 'decimal.js';

import { IDENTITYKEY } from '@/store/User';
import { AIFunction } from '@/utils/const';
import { TPlan } from '@/utils/eventlog/eventNewConfig';

export const FWebsiteEmail = 'support@falcocut.ai';

export interface NumberItem {
  id: string;
  value: number;
}

type CompareFunction = (value: number, target: number) => boolean;

export function findClosestNumber(
  items: NumberItem[] | number[],
  target: number,
  direction: 'smaller' | 'larger' = 'smaller',
): NumberItem | number | undefined {
  try {
    // 定义比较函数
    const compareFunc: CompareFunction =
      direction === 'smaller'
        ? (value, target) => value < target
        : (value, target) => value > target;

    // 统一处理数组格式
    const normalized: NumberItem[] =
      Array.isArray(items) && typeof items[0] === 'number'
        ? (items as number[]).map((num) => ({ id: String(num), value: num }))
        : (items as NumberItem[]);

    // 使用比较函数过滤
    const filtered = normalized.filter((item) => compareFunc(item.value, target));

    if (filtered.length === 0) return undefined;

    // 找到距离目标值最近的项
    const closest = filtered.reduce((closest, current) =>
      Math.abs(target - current.value) < Math.abs(target - closest.value)
        ? current
        : closest,
    );

    // 根据输入类型返回对应格式
    return Array.isArray(items) && typeof items[0] === 'number' ? closest.value : closest;
  } catch (error) {
    return 1;
  }
}

// 获取当前权益的上一个权益
export function getPrevVipType(type: IDENTITYKEY) {
  switch (type) {
    case 'freeVip':
      return 'Everything-in-Free';
    case 'basicVip':
      return 'Everything-in-Standard';
    case 'advancedVip':
      return 'Everything-in-Pro';
    case 'enterpriseVip':
      return '';
  }
}

// 获取支付类型
export const getPlanTag = (
  skuType: IDENTITYKEY,
  dateType: 'month' | 'year' | 'once' | 'twoweek',
): TPlan => {
  if (dateType === 'once') {
    return 'Credit';
  }

  if (skuType === 'basicVip') {
    if (dateType === 'month') return 'S1';
    if (dateType === 'year') return 'S2';
  }

  if (skuType === 'advancedVip') {
    if (dateType === 'month') return 'P1';
    if (dateType === 'year') return 'P2';
  }

  return 'Other';
};

export const formatMonthDisplayPrice = (
  raw: number,
  type: 'month' | 'year' = 'month',
) => {
  const num = new Decimal(raw);

  if (num.isZero()) return '0';

  // ① 如果是年价 → 换算到月价（先转成“分”再整除，保证分位精度）
  let value =
    type === 'year'
      ? num.times(100).div(12).floor().div(100) // 年价 *100 → 分，再 ÷12 → 月分，向下取整，最后 ÷100 → 月价
      : num;

  // ② 截断到 2 位小数（ROUND_DOWN ≡ 向下取整）
  value = value.toDecimalPlaces(2, Decimal.ROUND_DOWN);

  // ③ Decimal.toString() 默认会去掉尾随 0（如 16.60 → '16.6'，57.00 → '57'）
  return value.toString();

  // return result;
};

export const formatYearDisplayPrice = (raw: number, type: 'month' | 'year' = 'month') => {
  const num = new Decimal(formatMonthDisplayPrice(raw, type));
  return num.times(12).toDecimalPlaces(2, Decimal.ROUND_DOWN).toString();
};
